import type { LoaderFunction } from "react-router";
import { useEffect } from "react";
import { useLoaderData } from "react-router";
import { Box } from "@radix-ui/themes";
import { DemoThemeProvider } from "~/components/demo/demo-theme-provider";
import { AuthProvider } from "~/contexts/auth-context";
import HeroWithTyping from "~/components/demo/hero-with-typing";

interface InitialParams {
  primaryColor: string;
  autoPlay: boolean;
}

const DEFAULT_PARAMS: InitialParams = {
  primaryColor: "#4095A8",
  autoPlay: false,
};

export const loader: LoaderFunction = async ({ request }) => {
  const params = new URL(request.url).searchParams;

  return {
    initialParams: {
      ...DEFAULT_PARAMS,
      primaryColor: params.get("primaryColor") || "#4095A8",
      autoPlay: params.get("autoPlay") === "true",
    },
  };
};

function EmbedExplain() {
  const { initialParams } = useLoaderData<{ initialParams: InitialParams }>();

  // Send height updates to parent window for responsive iframe
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const sendHeight = () => {
      // Clear any pending updates
      if (timeoutId) clearTimeout(timeoutId);

      // Debounce to avoid excessive messages
      timeoutId = setTimeout(() => {
        // Get the actual rendered height of the embed container
        const embedContainer = document.querySelector(".embed-container");
        const height = embedContainer
          ? embedContainer.getBoundingClientRect().height + 16 // Add padding buffer
          : document.body.scrollHeight;

        window.parent.postMessage(
          {
            type: "pebble-embed-resize",
            height,
          },
          "*"
        );
      }, 50);
    };

    // Send initial height after content is rendered
    // Use multiple delays to catch content loading at different stages
    setTimeout(sendHeight, 100);
    setTimeout(sendHeight, 300);
    setTimeout(sendHeight, 500);

    // Send on window resize
    const handleResize = () => sendHeight();
    window.addEventListener("resize", handleResize);

    // Use ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(() => {
      sendHeight();
    });

    // Observe the embed container for more accurate measurements
    const embedContainer = document.querySelector(".embed-container");
    if (embedContainer) {
      resizeObserver.observe(embedContainer);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <AuthProvider>
      <DemoThemeProvider
        initialPrimaryColor={DEFAULT_PARAMS.primaryColor}
        initialAppearance="light"
      >
        <Box
          className="embed-container"
          style={{
            backgroundColor: "transparent",
            padding: "8px",
          }}
        >
          <style>
            {`
              html, body, #root {
                margin: 0;
                padding: 0;
                background: transparent !important;
                overflow: hidden !important; /* Prevent scrollbars on body */
              }

              /* Hide scrollbars but keep functionality */
              html {
                scrollbar-width: none; /* Firefox */
                -ms-overflow-style: none; /* IE and Edge */
              }
              
              html::-webkit-scrollbar,
              body::-webkit-scrollbar,
              #root::-webkit-scrollbar {
                display: none; /* Chrome, Safari, Opera */
                width: 0;
                height: 0;
              }

              /* Override Radix UI theme backgrounds */
              .radix-themes {
                background: transparent !important;
                background-color: transparent !important;
              }

              .embed-container {
                font-size: 0.9375rem;
                background: transparent !important;
                overflow: visible !important; /* Allow content to be measured properly */
                position: relative;
              }

              /* Hide any scrollbars that might appear in child elements during resize */
              .embed-container * {
                scrollbar-width: none; /* Firefox */
                -ms-overflow-style: none; /* IE and Edge */
              }
              
              .embed-container *::-webkit-scrollbar {
                display: none; /* Chrome, Safari, Opera */
              }

              .rt-PopoverContent,
              .rt-DialogContent,
              .rt-TooltipContent {
                background: var(--color-panel-solid) !important;
                background-color: var(--color-panel-solid) !important;
              }

              @media (max-width: 768px) {
                .embed-container {
                  padding: 4px !important;
                  font-size: 0.875rem;
                }
              }
            `}
          </style>
          <HeroWithTyping autoPlay={initialParams.autoPlay} />
        </Box>
      </DemoThemeProvider>
    </AuthProvider>
  );
}

export default EmbedExplain;
